// =============================================================================
// src/components/profile/DevicesSection.jsx
//
// "My Devices" section for the Profile modal.
// Lists all paired AuditPrOS Sync agents for the logged-in user, with
// last-seen timestamp, online indicator, and a Revoke action.
//
// Endpoints used:
//   GET    /agents/mine                 → list devices
//   DELETE /agents/:agentId             → revoke a device
//
// Both added in M1 (routes/agents.js). If they return 404, we show a gentle
// "feature not available" notice rather than crashing.
// =============================================================================

import React, { useState, useEffect, useCallback } from "react";
import { PageWrap } from './UI';
import '../styles/DevicesSection.css';
import '../styles/Profile.css';

const BASE = process.env.REACT_APP_API_URL || "http://localhost:3000";

function getAuthHeaders() {
  try {
    const saved = localStorage.getItem("auditpros_user");
    if (!saved) return {};
    const parsed = JSON.parse(saved);
    const account = parsed?.account;
    const email =
      account?.idTokenClaims?.email ||
      account?.idTokenClaims?.preferred_username ||
      account?.idTokenClaims?.emails?.[0] ||
      account?.username ||
      account?.email;
    const h = { "Content-Type": "application/json" };
    if (email) h["x-user-email"] = email;
    return h;
  } catch {
    return {};
  }
}

function fmtRelative(dateStr) {
  if (!dateStr) return "never";
  const then = new Date(dateStr);
  const diffMs = Date.now() - then.getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return "just now";
  if (sec < 3600) return `${Math.floor(sec / 60)} min ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)} hr ago`;
  return then.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function DevicesSection() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [revoking, setRevoking] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${BASE}/agents/mine`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        if (res.status === 404) {
          setError("unavailable");
        } else {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || `HTTP ${res.status}`);
        }
        return;
      }
      const data = await res.json();
      setAgents(Array.isArray(data) ? data : data.agents || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRevoke = async (agentId, deviceName) => {
    if (
      !window.confirm(
        `Revoke "${deviceName}"?\n\nThis will disconnect the sync agent on that computer. To use AuditPrOS Sync again, you'll need to re-pair it.`,
      )
    )
      return;

    setRevoking((r) => ({ ...r, [agentId]: true }));
    try {
      const res = await fetch(`${BASE}/agents/${agentId}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      setAgents((list) => list.filter((a) => a.id !== agentId));
    } catch (err) {
      alert(`Failed to revoke: ${err.message}`);
    } finally {
      setRevoking((r) => {
        const n = { ...r };
        delete n[agentId];
        return n;
      });
    }
  };

  return (
    <PageWrap>
      <div className="pf-page-title">My Devices</div>
      <p className="pf-page-sub">
        AuditPrOS Sync is a small app that runs in your Windows system tray and
        securely syncs your local Tally data to AuditPrOS. Install it once on
        each computer you want to sync from.
      </p>

      {/* Download card — prominent */}
      <div className="pf-card ds-download-card">
        <div className="ds-download-body">
          <div className="ds-download-icon">↓</div>
          <div className="ds-download-info">
            <div className="ds-download-title">Get AuditPrOS Sync for Windows</div>
            <div className="ds-download-desc">
              Runs quietly in your system tray. Reads from Tally only — never
              modifies your data. Small installer, no admin access needed.
            </div>
          </div>
          <a
            href={process.env.REACT_APP_TALLY_CONNECTOR_URL}
            download="AuditPrOS-Tally-Connector-Setup.exe"
            className="pf-btn pf-btn-primary ds-download-link"
          >
            Download for Windows
          </a>
        </div>
      </div>

      {/* Paired devices list */}
      <div className="pf-card">
        <div className="pf-card-title ds-card-title-row">
          <span>Paired Devices {agents.length > 0 && `(${agents.length})`}</span>
          <button
            onClick={load}
            disabled={loading}
            className="pf-btn pf-btn-outline ds-refresh-btn"
          >
            {loading ? "…" : "↺ Refresh"}
          </button>
        </div>

        {error === "unavailable" ? (
          <div className="pf-alert pf-alert-info ds-alert-no-mb">
            Device management will be available once the agent infrastructure is
            deployed.
          </div>
        ) : error ? (
          <div className="pf-alert pf-alert-error ds-alert-no-mb">{error}</div>
        ) : loading ? (
          <div className="ds-loading">Loading devices…</div>
        ) : agents.length === 0 ? (
          <div className="ds-empty">
            <div className="ds-empty-icon">🖥️</div>
            <div className="ds-empty-title">No devices paired yet</div>
            <div className="ds-empty-sub">
              Install AuditPrOS Sync on your PC and follow the pairing flow to
              add a device here.
            </div>
          </div>
        ) : (
          <div className="ds-agent-list">
            {agents.map((agent) => {
              const online = agent.is_online ?? agent.isOnline ?? false;
              const revokingThis = !!revoking[agent.id];
              return (
                <div key={agent.id} className="ds-agent-row">
                  <div className="ds-agent-icon">🖥️</div>

                  <div className="ds-agent-info">
                    <div className="ds-agent-name">
                      {agent.device_name || agent.deviceName || "Unknown device"}
                    </div>
                    <div className="ds-agent-meta">
                      {agent.platform || "windows"}
                      {agent.agent_version && ` · v${agent.agent_version}`}
                      {" · paired "}
                      {fmtRelative(agent.paired_at || agent.pairedAt)}
                    </div>
                  </div>

                  <div className={`ds-agent-status ds-agent-status--${online ? 'online' : 'offline'}`}>
                    <span className={`ds-agent-dot ds-agent-dot--${online ? 'online' : 'offline'}`} />
                    {online
                      ? "Online"
                      : `Offline — last seen ${fmtRelative(agent.last_seen_at || agent.lastSeenAt)}`}
                  </div>

                  <button
                    onClick={() =>
                      handleRevoke(
                        agent.id,
                        agent.device_name || agent.deviceName || "this device",
                      )
                    }
                    disabled={revokingThis}
                    className="pf-btn pf-btn-outline ds-revoke-btn"
                    style={{
                      color: revokingThis ? "var(--text3)" : "var(--red)",
                      borderColor: revokingThis ? "var(--border)" : "var(--red)",
                    }}
                  >
                    {revokingThis ? "Revoking…" : "Revoke"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Help */}
      <div className="pf-card ds-help-card">
        <div className="pf-card-title">Getting started</div>

        {[
          {
            n: "01",
            t: "Download and install AuditPrOS Tally Connector on the computer that has your Tally installation.",
          },
          {
            n: "02",
            t: "AuditPrOS Tally Connector sits in the system tray (near the clock). A green icon means it's ready to sync.",
          },
          {
            n: "03",
            t: "First time, the Tally Connector pairs automatically with the logged in account in AuditPrOS.",
          },
          {
            n: "04",
            t: 'To change agent account, right click on Tally Connector on System Tray and unpair. It opens your browser to pair with your current AuditPrOS account — just sign in and click "Pair this device".',
          },
          {
            n: "05",
            t: "Trigger syncs from the Audit Requests page whenever you need fresh Tally data.",
          },
        ].map((s) => (
          <div key={s.n} className="pf-row ds-step-row">
            <span className="ds-step-num">{s.n}</span>
            <span className="ds-step-text">{s.t}</span>
          </div>
        ))}
      </div>
    </PageWrap>
  );
}
